import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCase, useCaseSubmit } from "../../context/CaseContext";
import { Stage } from "../../shared/CaseConfiguration";
import {
  lastClosedStageOf,
  openStageOf,
  todayIso,
  withReopened,
} from "../../shared/timeline";
import { YesNoDialog } from "../general/YesNoDialog";
import { decisionsOf } from "../../shared/judgmentDecisions";
import { judgmentOf } from "../../shared/judgment";
import { CloseEnforcementDialog } from "./CloseEnforcementDialog";
import { CloseStageDialog } from "./CloseStageDialog";
import { CloseTrialDialog } from "./CloseTrialDialog";

/**
 * Where the open case stands on its timeline, and how its user moves it on:
 * closing the open stage, with the date it ended and what was spent on it,
 * or opening the stage closed last again. The timeline decides where a case
 * stands; the Cost progress slider moves within the open stage only and asks
 * for these where it is dragged beyond it.
 */
export function useStageClosing(): {
  open?: Stage;
  lastClosed?: Stage;
  requestClose: () => void;
  requestReopen: () => void;
  requestJudgment: () => void;
  dialogs: ReactNode;
} {
  const { t } = useTranslation(undefined, { keyPrefix: "case.timeline" });
  const { t: tRoot } = useTranslation();
  const caseContext = useCase();
  const submit = useCaseSubmit();
  const stages = caseContext.getStages();
  const timeline = caseContext.getCase().timeline;
  const open = openStageOf(timeline, stages);
  const lastClosed = lastClosedStageOf(timeline, stages);
  const [closing, setClosing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [editing, setEditing] = useState(false);
  const stageName = (stage: Stage) => tRoot(`home.stages.${Stage[stage]}`);

  return {
    open,
    lastClosed,
    requestClose: () => open !== undefined && setClosing(true),
    requestReopen: () => lastClosed !== undefined && setReopening(true),
    requestJudgment: () => setEditing(true),
    dialogs: (
      <>
        {closing &&
          open !== undefined &&
          (open === Stage.Trial &&
          decisionsOf(caseContext.getCase()).length > 0 ? (
            <CloseTrialDialog
              today={todayIso()}
              onClose={() => setClosing(false)}
            />
          ) : open === Stage.Enforcement &&
            judgmentOf(caseContext.getCase()) ? (
            <CloseEnforcementDialog
              today={todayIso()}
              onClose={() => setClosing(false)}
            />
          ) : (
            <CloseStageDialog
              stage={open}
              today={todayIso()}
              onClose={() => setClosing(false)}
            />
          ))}
        {editing && (
          <CloseTrialDialog
            today={todayIso()}
            editing
            onClose={() => setEditing(false)}
          />
        )}
        {lastClosed !== undefined && (
          <YesNoDialog
            open={reopening}
            title={t("reopening.title", { stage: stageName(lastClosed) })}
            description={t("reopening.question", {
              stage: stageName(lastClosed),
            })}
            onYes={() => {
              setReopening(false);
              submit(
                {
                  timeline: JSON.stringify(withReopened(timeline, lastClosed)),
                },
                { method: "put" },
              );
            }}
            onNo={() => setReopening(false)}
          />
        )}
      </>
    ),
  };
}
